import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, Plus, Trash2, RotateCcw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { US_STATES } from "@/utils/statesList";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  managementCompanyApi,
  type Closure,
  type OperatingHoursDay,
} from "@/services/api";

const DAYS = [
  { dow: 1, label: "Monday" },
  { dow: 2, label: "Tuesday" },
  { dow: 3, label: "Wednesday" },
  { dow: 4, label: "Thursday" },
  { dow: 5, label: "Friday" },
  { dow: 6, label: "Saturday" },
  { dow: 0, label: "Sunday" },
];

// The zones Dommek's management companies actually operate in.
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
];

function defaultWeek(): OperatingHoursDay[] {
  return DAYS.map(({ dow }) => ({
    day_of_week: dow,
    is_open: dow >= 1 && dow <= 5,
    opens_at: dow >= 1 && dow <= 5 ? "09:00" : null,
    closes_at: dow >= 1 && dow <= 5 ? "17:00" : null,
  }));
}

function describeClosure(closure: Closure): string {
  if (closure.closure_date) return closure.closure_date;
  if (closure.recurring_month && closure.recurring_day) {
    const mm = String(closure.recurring_month).padStart(2, "0");
    const dd = String(closure.recurring_day).padStart(2, "0");
    return `Every ${mm}/${dd}`;
  }
  return "—";
}

interface Props {
  managementCompanyId: number;
}

export default function OperatingHoursTab({ managementCompanyId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["management-company-operating-hours", managementCompanyId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => managementCompanyApi.getOperatingHours(managementCompanyId),
    enabled: Boolean(managementCompanyId),
  });

  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState("America/New_York");
  const [week, setWeek] = useState<OperatingHoursDay[]>(defaultWeek());

  const [newLabel, setNewLabel] = useState("");
  const [newDate, setNewDate] = useState("");
  const [repeatsYearly, setRepeatsYearly] = useState(false);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.operating_hours_enabled);
    setTimezone(data.timezone || "America/New_York");

    const fromServer = new Map(data.hours.map((h) => [h.day_of_week, h]));
    setWeek(
      defaultWeek().map((fallback) => {
        const saved = fromServer.get(fallback.day_of_week);
        return saved
          ? {
              day_of_week: saved.day_of_week,
              is_open: saved.is_open,
              opens_at: saved.opens_at?.slice(0, 5) ?? null,
              closes_at: saved.closes_at?.slice(0, 5) ?? null,
            }
          : fallback;
      })
    );
  }, [data]);

  const closures = useMemo(
    () =>
      [...(data?.closures ?? [])].sort((a, b) =>
        describeClosure(a).localeCompare(describeClosure(b))
      ),
    [data]
  );

  const dismissed = useMemo(
    () =>
      [...(data?.dismissed ?? [])].sort((a, b) =>
        describeClosure(a).localeCompare(describeClosure(b))
      ),
    [data]
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const saveHours = useMutation({
    mutationFn: () =>
      managementCompanyApi.saveOperatingHours(managementCompanyId, {
        timezone,
        operating_hours_enabled: enabled,
        hours: week,
      }),
    onSuccess: () => {
      toast({
        title: "Operating hours saved",
        description: enabled
          ? "Delivery dates for this company now follow this schedule."
          : "Delivery dates still use the default Monday to Friday calendar.",
      });
      invalidate();
    },
    onError: (error: unknown) =>
      toast({
        title: "Could not save operating hours",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      }),
  });

  const addClosure = useMutation({
    mutationFn: () => {
      const [, month, day] = newDate.split("-");
      return managementCompanyApi.addClosure(managementCompanyId, {
        label: newLabel.trim() || "Closed",
        closure_date: repeatsYearly ? null : newDate,
        recurring_month: repeatsYearly ? Number(month) : null,
        recurring_day: repeatsYearly ? Number(day) : null,
      });
    },
    onSuccess: () => {
      setNewLabel("");
      setNewDate("");
      setRepeatsYearly(false);
      invalidate();
    },
    onError: (error: unknown) =>
      toast({
        title: "Could not add that day",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      }),
  });

  const removeClosure = useMutation({
    mutationFn: (closureId: number) =>
      managementCompanyApi.deleteClosure(managementCompanyId, closureId),
    onSuccess: (result) => {
      if (result.dismissed) {
        toast({
          title: "Holiday removed",
          description:
            "Reloading the federal holidays will not bring it back. You can put it back below.",
        });
      }
      invalidate();
    },
  });

  const restoreClosure = useMutation({
    mutationFn: (closureId: number) =>
      managementCompanyApi.restoreClosure(managementCompanyId, closureId),
    onSuccess: invalidate,
  });

  const generateHolidays = useMutation({
    mutationFn: (state?: string) =>
      managementCompanyApi.generateHolidays(managementCompanyId, { years: 3, state }),
    onSuccess: (result) => {
      toast({
        title: "Holidays imported",
        description:
          `${result.generated} days added for the next ${result.years} years.` +
          (result.skipped
            ? ` ${result.skipped} were left as you entered them by hand.`
            : " Days you added by hand were left alone."),
      });
      invalidate();
    },
    onError: (error: unknown) =>
      toast({
        title: "Could not load the holidays",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      }),
  });

  const updateDay = (dow: number, patch: Partial<OperatingHoursDay>) =>
    setWeek((current) =>
      current.map((d) => (d.day_of_week === dow ? { ...d, ...patch } : d))
    );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading operating hours…
      </div>
    );
  }

  return (
    <div
      className="space-y-8"
      // Enter in an input must not submit the management-company form that
      // wraps this tab; every action here is its own mutation.
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
          e.preventDefault();
        }
      }}
    >
      {/* ---------------------------------------------------------------- */}
      <section className="rounded-lg border p-5">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="font-medium">Use this company's own calendar</h3>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Delivery dates are counted in open days and the closing time acts
              as the order cutoff. While this is off, delivery follows the
              default calendar: Monday to Friday, Eastern Time, no holidays.
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div className="mt-5 max-w-xs">
          <Label htmlFor="mc-timezone">Timezone</Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger id="mc-timezone" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-muted-foreground">
            The hours below are in this company's local time.
          </p>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="rounded-lg border p-5">
        <h3 className="font-medium">Business hours</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Orders placed after closing count against the next open day.
        </p>

        <div className="mt-4 divide-y">
          {DAYS.map(({ dow, label }) => {
            const day = week.find((d) => d.day_of_week === dow)!;
            return (
              <div key={dow} className="flex items-center gap-4 py-3">
                <div className="flex w-40 items-center gap-3">
                  <Switch
                    checked={day.is_open}
                    onCheckedChange={(isOpen) =>
                      updateDay(dow, {
                        is_open: isOpen,
                        opens_at: isOpen ? day.opens_at ?? "09:00" : null,
                        closes_at: isOpen ? day.closes_at ?? "17:00" : null,
                      })
                    }
                    aria-label={label}
                  />
                  <span className="text-sm font-medium">{label}</span>
                </div>

                {day.is_open ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      className="w-32"
                      value={day.opens_at ?? ""}
                      onChange={(e) =>
                        updateDay(dow, { opens_at: e.target.value })
                      }
                      aria-label={`${label} opens at`}
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      className="w-32"
                      value={day.closes_at ?? ""}
                      onChange={(e) =>
                        updateDay(dow, { closes_at: e.target.value })
                      }
                      aria-label={`${label} closes at`}
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Closed</span>
                )}
              </div>
            );
          })}
        </div>

        <Button type="button"
          className="mt-5"
          onClick={() => saveHours.mutate()}
          disabled={saveHours.isPending}
        >
          {saveHours.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Save hours
        </Button>
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="rounded-lg border p-5">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="font-medium">Holidays</h3>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              These are skipped when counting delivery days. Six of the eleven
              federal holidays move every year, so loading them writes real
              dates rather than a rule.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="closure-label">Name</Label>
            <Input
              id="closure-label"
              className="mt-1.5 w-56"
              placeholder="Holiday name"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="closure-date">Date</Label>
            <Input
              id="closure-date"
              type="date"
              className="mt-1.5 w-44"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 pb-2.5">
            <Switch
              id="closure-repeats"
              checked={repeatsYearly}
              onCheckedChange={setRepeatsYearly}
            />
            <Label htmlFor="closure-repeats" className="text-sm font-normal">
              Every year on this day
            </Label>
          </div>
          <Button type="button"
            className="mb-0.5"
            onClick={() => addClosure.mutate()}
            disabled={!newDate || addClosure.isPending}
          >
            {addClosure.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Add
          </Button>

          <div className="mx-1 hidden h-9 w-px bg-border sm:block" />

          {/* Import instead of Load, with the arrow, and a list to pick from:
              the federal set, or federal plus one state's own. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" className="mb-0.5" disabled={generateHolidays.isPending}>
                {generateHolidays.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Import Holidays
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
              <DropdownMenuItem onSelect={() => generateHolidays.mutate(undefined)}>
                US Federal holidays
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Federal plus the state's own</DropdownMenuLabel>
              {US_STATES.map((st) => (
                <DropdownMenuItem
                  key={st.value}
                  onSelect={() => generateHolidays.mutate(st.value)}
                >
                  {st.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {closures.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">
            No holidays yet.
          </p>
        ) : (
          <ul className="mt-6 divide-y border-t">
            {closures.map((closure) => (
              <li
                key={closure.id}
                className="flex items-center justify-between gap-4 py-2.5"
              >
                <div className="flex items-baseline gap-3">
                  <span className="w-32 font-mono text-sm tabular-nums">
                    {describeClosure(closure)}
                  </span>
                  <span className="text-sm">{closure.label}</span>
                  {closure.source === "federal" && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      federal
                    </span>
                  )}
                </div>
                <Button type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    closure.id && removeClosure.mutate(closure.id)
                  }
                  aria-label={`Remove ${closure.label}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {dismissed.length > 0 && (
          <div className="mt-8 border-t pt-5">
            <h4 className="text-sm font-medium">
              Holidays this office works
            </h4>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Removed on purpose, so reloading the federal holidays leaves them
              out. Put one back if that changes.
            </p>
            <ul className="mt-3 divide-y border-t">
              {dismissed.map((closure) => (
                <li
                  key={closure.id}
                  className="flex items-center justify-between gap-4 py-2.5 text-muted-foreground"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="w-32 font-mono text-sm tabular-nums line-through">
                      {describeClosure(closure)}
                    </span>
                    <span className="text-sm">{closure.label}</span>
                  </div>
                  <Button type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      closure.id && restoreClosure.mutate(closure.id)
                    }
                    aria-label={`Put ${closure.label} back`}
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    Put back
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
